import { ServiceAreaUploadReturnModel } from "@app/projects/create/interfaces";
import RegionSet, { RegionGranularity } from "@app/region-sets/regionSet";
import ServiceBase from "@app/shared/serviceBase";
import { querystringify } from "@app/shared/utility/querystringify";

/** Manages access to CRUD functionality for region sets. */
export default class RegionSetService extends ServiceBase {
  /**
   * Creates / returns a region set with a `regionSetId` populated from a region set.
   * If no region set exists, then a new `regionSetId` is created and attached to the returned object.
   * @param regionSet The region set we're creating / retrieving.
   */
  public async create(regionSet: RegionSet): Promise<RegionSet> {
    return await this.makePost<RegionSet>("regionsets/getorcreate", regionSet);
  }

  /**
   * Retrieves a region set by a given identifier.
   * @param id The identifier.
   */
  public async getById(id: number): Promise<RegionSet> {
    return await this.makeGet<RegionSet>(`regionsets/${id}`);
  }

  /**
   * Retrieves region sets, given a collection of region set identifiers.
   * @param ids The identifiers.
   */
  public async getByIds(ids: number[]): Promise<RegionSet[]> {
    return await this.makePost<RegionSet[]>("regionsets", ids);
  }

  /**
   * Retrieves all region sets included inside the set specificed by `id`.
   * Filtered to the target granularity.
   * @param id The identifier of the parent region set.
   * @param granularity The granularity to filter by.
   */
  public async getByGranularity(id: number, granularity: RegionGranularity): Promise<RegionSet[]> {
    return await this.makeGet<RegionSet[]>(`regionsets/${id}/bygranularity/${granularity}`);
  }

  /**
   * Retrieves all the leaves associated with the given region set.
   * @param id The identifier of the provided region set.
   */
  public async getLeafNodes(id: number): Promise<RegionSet[]> {
    return await this.makeGet<RegionSet[]>(`regionsets/${id}/leafnodes`);
  }

  /**
   * Retrieves allthe nodes associated with the given region set.
   * @param id The identifier of the provided region set.
   */
  public async getNodes(id: number): Promise<RegionSet[]> {
    return await this.makeGet<RegionSet[]>(`regionsets/${id}/nodes`);
  }

  /**
   * returns a region set object model populated from an imported service area file(.qsa).
   * @param data The service area file data.
   */
  public async createFromFile(serviceAreaFile: FormData, isZipCodeProject = false): Promise<ServiceAreaUploadReturnModel> {
    return await this.makePost<ServiceAreaUploadReturnModel>(`regionsets/translateserviceareaintoregionset?${querystringify({ isZipCodeProject })}`, serviceAreaFile, undefined, true, false, false);
  }
}
